"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Money, formatMoney } from "@/components/common/Money";
import { Plus, Trash2, Shirt, Calculator } from "lucide-react";

interface OrderItem {
  id: string;
  garmentType: string;
  qty: number;
  tagCode?: string | null;
}

interface LineItem {
  alterationType: string;
  unitPrice: number;
  qty: number;
  notes?: string;
}

interface ItemLines {
  orderItemId: string;
  lines: LineItem[];
}

interface QuoteBuilderProps {
  items: OrderItem[];
  deliveryFee: number;
  onSave: (data: { items: ItemLines[] }) => Promise<void>;
  saving?: boolean;
}

export function QuoteBuilder({
  items,
  deliveryFee,
  onSave,
  saving = false,
}: QuoteBuilderProps) {
  const [itemLines, setItemLines] = useState<Record<string, LineItem[]>>(
    items.reduce((acc, item) => {
      acc[item.id] = [{ alterationType: "", unitPrice: 0, qty: 1, notes: "" }];
      return acc;
    }, {} as Record<string, LineItem[]>)
  );

  const addLine = (itemId: string) => {
    setItemLines((prev) => ({
      ...prev,
      [itemId]: [
        ...(prev[itemId] || []),
        { alterationType: "", unitPrice: 0, qty: 1, notes: "" },
      ],
    }));
  };

  const removeLine = (itemId: string, lineIndex: number) => {
    setItemLines((prev) => ({
      ...prev,
      [itemId]: prev[itemId].filter((_, i) => i !== lineIndex),
    }));
  };

  const updateLine = (
    itemId: string,
    lineIndex: number,
    field: keyof LineItem,
    value: string | number
  ) => {
    setItemLines((prev) => ({
      ...prev,
      [itemId]: prev[itemId].map((line, i) =>
        i === lineIndex ? { ...line, [field]: value } : line
      ),
    }));
  };

  const calculateSubtotal = () => {
    return Object.values(itemLines)
      .flat()
      .reduce((sum, line) => sum + line.unitPrice * line.qty, 0);
  };

  const subtotal = calculateSubtotal();
  const tax = 0;
  const total = subtotal + tax + deliveryFee;

  const handleSave = async () => {
    const data = {
      items: Object.entries(itemLines)
        .filter(([_, lines]) => lines.some((l) => l.alterationType.trim()))
        .map(([orderItemId, lines]) => ({
          orderItemId,
          lines: lines.filter((l) => l.alterationType.trim()),
        })),
    };

    if (data.items.length === 0) {
      return;
    }

    await onSave(data);
  };

  const isValid = Object.values(itemLines).some((lines) =>
    lines.some((l) => l.alterationType.trim() && l.unitPrice >= 0 && l.qty > 0)
  );

  return (
    <div className="space-y-6">
      {items.map((item) => (
        <Card key={item.id}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shirt className="h-4 w-4" />
              {item.garmentType}
              {item.tagCode && (
                <span className="text-sm font-normal text-muted-foreground">
                  (Tag: {item.tagCode})
                </span>
              )}
              <span className="text-sm font-normal text-muted-foreground ml-auto">
                Qty: {item.qty}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(itemLines[item.id] || []).map((line, lineIndex) => (
              <div
                key={lineIndex}
                className="grid gap-4 sm:grid-cols-[1fr_100px_80px_auto] items-end"
              >
                <div>
                  <Label className="text-xs">Alteration Type</Label>
                  <Input
                    placeholder="e.g., Hem shortening"
                    value={line.alterationType}
                    onChange={(e) =>
                      updateLine(item.id, lineIndex, "alterationType", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs">Unit Price</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={line.unitPrice || ""}
                    onChange={(e) =>
                      updateLine(
                        item.id,
                        lineIndex,
                        "unitPrice",
                        parseFloat(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs">Qty</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={line.qty}
                    onChange={(e) =>
                      updateLine(
                        item.id,
                        lineIndex,
                        "qty",
                        parseInt(e.target.value) || 1
                      )
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => removeLine(item.id, lineIndex)}
                  disabled={(itemLines[item.id] || []).length <= 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addLine(item.id)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Line
            </Button>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Quote Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <Money amount={subtotal} />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Tax</span>
            <Money amount={tax} />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Delivery Fee</span>
            <Money amount={deliveryFee} />
          </div>
          <Separator />
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <Money amount={total} />
          </div>

          <Button
            className="w-full mt-4"
            onClick={handleSave}
            disabled={!isValid || saving}
          >
            {saving ? "Saving..." : "Save Draft Quote"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
